import { z } from 'zod';
import { PseudonymSchema } from './common';
import { ReactionSchema, SparkDirectionSchema } from './spark';
import { VibeTagSchema } from './profile';

export const WHISPER_MAX = 500;
/** Max stored image size. The client downscales photos to ~200–600 KB before
 * upload; this ceiling only bites a raw upload on a browser where downscaling
 * can't run. Well under the Workers 100 MB request-body limit — the real
 * constraint is R2 storage. Shared by Whisper and Pro DMs. */
export const WHISPER_IMAGE_MAX_BYTES = 8 * 1024 * 1024;
export const IMAGE_MAX_MB = WHISPER_IMAGE_MAX_BYTES / 1024 / 1024;
export const WHISPER_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const;

export const WhisperStatusSchema = z.enum(['pending', 'accepted', 'declined']);
export type WhisperStatus = z.infer<typeof WhisperStatusSchema>;

export const WhisperMessageSchema = z.object({
  id: z.string(),
  /** "me" or "them" — never a real identity. */
  from: z.enum(['me', 'them']),
  text: z.string().max(WHISPER_MAX),
  /** Opaque R2 key for an image message (`text` is empty when set). Fetch the
   * actual bytes from `/api/whisper/:threadId/messages/:id/image` — never a
   * public URL. */
  imageKey: z.string().nullable(),
  /** true when a moderator removed the image via the admin media manager —
   * `imageKey` is null and the client shows an "image removed" placeholder. */
  imageRemoved: z.boolean(),
  reaction: ReactionSchema.nullable(),
  createdAt: z.iso.datetime(),
});
export type WhisperMessage = z.infer<typeof WhisperMessageSchema>;

export const WhisperThreadSummarySchema = z.object({
  id: z.string(),
  /** The other side's pseudonym in this thread. */
  otherPseudonym: PseudonymSchema,
  /** Where the first spark was sent from. */
  spotName: z.string(),
  lastMessagePreview: z.string().max(80),
  lastMessageAt: z.iso.datetime(),
  unread: z.boolean(),
  /** true if the current user was the original admirer. */
  iStarted: z.boolean(),
  blocked: z.boolean(),
  /** pending → the receiver hasn't accepted yet. */
  status: WhisperStatusSchema,
  /** Where the admirer said they were standing (receiver's POV). */
  direction: SparkDirectionSchema.nullable(),
  /** The first line — shown as the request preview before accepting. */
  openingLine: z.string().max(200).nullable(),
});
export type WhisperThreadSummary = z.infer<typeof WhisperThreadSummarySchema>;

/* GET /api/whisper?box=received|sent */
export const WhisperInboxResponseSchema = z.object({
  threads: z.array(WhisperThreadSummarySchema),
});
export type WhisperInboxResponse = z.infer<typeof WhisperInboxResponseSchema>;

/** How to find the admirer — shown to the receiver on a pending request. */
export const OpenerLookSchema = z.object({
  direction: SparkDirectionSchema.nullable(),
  appearanceBlurb: z.string().max(80).nullable(),
  section: z.string().max(24).nullable(),
  vibeEmoji: z.string().max(8),
  tags: z.array(VibeTagSchema).max(5),
});
export type OpenerLook = z.infer<typeof OpenerLookSchema>;

/* GET /api/whisper/:id */
export const WhisperThreadResponseSchema = z.object({
  thread: WhisperThreadSummarySchema,
  messages: z.array(WhisperMessageSchema),
  /** Present only for the receiver of a still-pending request. */
  openerLook: OpenerLookSchema.nullable(),
});
export type WhisperThreadResponse = z.infer<typeof WhisperThreadResponseSchema>;

/* POST /api/whisper/:id/messages */
export const PostWhisperMessageSchema = z.object({
  text: z.string().trim().min(1).max(WHISPER_MAX),
});
export type PostWhisperMessage = z.infer<typeof PostWhisperMessageSchema>;

/* POST /api/whisper/:id/react */
export const ReactWhisperSchema = z.object({
  messageId: z.string(),
  reaction: ReactionSchema.nullable(),
});
export type ReactWhisper = z.infer<typeof ReactWhisperSchema>;

/**
 * GET /api/whisper/:id/locate — precise live bearing/distance to the other
 * side of an *accepted* thread, only while you're both currently checked into
 * the Spot the match happened at. Unlike Radar (bucketed distance, fuzzed
 * angle, rotating pseudonyms — see `NearbyPerson`), this exposes real metres
 * and a real compass bearing: the pair already double-opted in by matching,
 * so the anti-stalking fuzzing that protects strangers on Radar doesn't apply
 * here. Nothing new is stored — this reads the same ephemeral, auto-expiring
 * `check_ins` heartbeat Radar already uses.
 */
export const LocateResponseSchema = z.object({
  available: z.boolean(),
  /** Great-circle distance in metres. Null when `available` is false. */
  distanceM: z.number().nullable(),
  /** Absolute compass bearing (0 = north) from you to them. Null when `available` is false. */
  bearingDeg: z.number().nullable(),
  viewerAccuracyM: z.number().nullable(),
  peerAccuracyM: z.number().nullable(),
});
export type LocateResponse = z.infer<typeof LocateResponseSchema>;
